import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { API } from '@services/api.service';
import {
  DATE_FORMATS,
  formatDateForBackend,
  getSelectedWorkdaysCount,
} from '@/utils/formUtils';

/**
 * Shift-aware leave day counter.
 *
 * For regular employees the leave day count is the number of workdays in the
 * range (weekends/holidays excluded). For shift workers (allowWeekendLeaves
 * effective) the count must instead be the number of days they are actually
 * rostered to work within the range — worked weekends count, rostered days off
 * do not. That rota-based number is computed on the backend from the employee's
 * timesheet; when no rota covers the range we fall back to the calendar-day
 * count (the previous behaviour).
 *
 * Used by every leave request form so the displayed and submitted `numOfDays`
 * match what the payroll overlay actually consumes.
 *
 * @param {Object} params
 * @param {string} params.employeeId
 * @param {Array} params.dateOfLeaveValues - [dateFrom, dateTo] (dayjs values).
 * @param {boolean} params.allowWeekendLeaves
 * @param {Array} [params.publicHolidays]
 * @returns {number|null} The leave day count, or null when no range is selected.
 */
export const useLeaveDaysCount = ({
  employeeId,
  dateOfLeaveValues,
  allowWeekendLeaves,
  publicHolidays = [],
}) => {
  // Rota-based rostered-day count for shift workers; null = not applicable /
  // no rota → fall back to the calendar/workday count.
  const [shiftLeaveDays, setShiftLeaveDays] = useState(null);

  const { mutateAsync: mutateLeaveDays } = useMutation({
    mutationFn: async (payload) => API.requests.computeLeaveDays(payload),
    onSuccess: (response) => {
      setShiftLeaveDays(
        response?.hasRota ? Number(response.scheduled) || 0 : null
      );
    },
    onError: (error) => {
      console.error('Failed to compute rota-based leave days:', error);
      setShiftLeaveDays(null);
    },
  });

  useEffect(() => {
    if (!allowWeekendLeaves || !employeeId) {
      setShiftLeaveDays(null);
      return;
    }
    if (!dateOfLeaveValues || dateOfLeaveValues.length !== 2) {
      setShiftLeaveDays(null);
      return;
    }
    const [dateFrom, dateTo] = dateOfLeaveValues;
    if (!dateFrom || !dateTo) {
      setShiftLeaveDays(null);
      return;
    }

    mutateLeaveDays({
      employeeId,
      startDate: formatDateForBackend(dateFrom, DATE_FORMATS.STANDARD),
      endDate: formatDateForBackend(dateTo, DATE_FORMATS.STANDARD),
    });
  }, [dateOfLeaveValues, allowWeekendLeaves, employeeId, mutateLeaveDays]);

  return useMemo(() => {
    // Shift workers: prefer the rota-based count when the rota covers the range;
    // otherwise fall back to the calendar/workday count.
    if (allowWeekendLeaves && shiftLeaveDays != null) {
      return shiftLeaveDays;
    }
    return getSelectedWorkdaysCount(
      dateOfLeaveValues,
      allowWeekendLeaves,
      publicHolidays
    );
  }, [dateOfLeaveValues, allowWeekendLeaves, publicHolidays, shiftLeaveDays]);
};

export default useLeaveDaysCount;
